package com.schoolconnect.app.feature.examresults

import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.datasource.network.response.TeacherExamResultListItemResponse
import com.schoolconnect.app.repository.examresult.ExamResultRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ExamResultsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(ExamResultsUiState(isLoading = true))
    val uiState: StateFlow<ExamResultsUiState> = _uiState.asStateFlow()

    init { loadPage() }

    fun onEvent(event: ExamResultsUiEvent) {
        when (event) {
            is ExamResultsUiEvent.SearchChanged -> { _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }; loadPage() }
            is ExamResultsUiEvent.FiltersChanged -> _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }
            ExamResultsUiEvent.FiltersApplied -> loadPage()
            ExamResultsUiEvent.FiltersReset -> { _uiState.update { it.copy(searchQuery = "", filters = ExamResultFilters(), pageIndex = 0) }; loadPage() }
            is ExamResultsUiEvent.PageChanged -> { _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }; loadPage() }
            is ExamResultsUiEvent.PageSizeChanged -> { _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; loadPage() }
            is ExamResultsUiEvent.SortChanged -> { _uiState.update { state ->
                val ascending = if (state.sortField == event.field) !state.sortAscending else true
                state.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0)
            }; loadPage() }
            is ExamResultsUiEvent.DeleteClicked, is ExamResultsUiEvent.UpdateClicked -> Unit
        }
    }

    private fun loadPage() {
        loadJob?.cancel()
        val state = _uiState.value
        loadJob = scope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            ExamResultRepository.loadTeacherPage(
                search = state.searchQuery,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc",
                refreshScope = scope
            ).onSuccess { response ->
                _uiState.update { it.copy(
                    rows = response.items.orEmpty().map { item -> item.toUi() },
                    totalCount = response.counters?.total ?: response.items.orEmpty().size,
                    isLoading = false,
                    errorMessage = ""
                ) }
            }.onFailure { throwable ->
                _uiState.update { it.copy(isLoading = false, errorMessage = throwable.message ?: "Unable to load exam results") }
            }
        }
    }

    fun clear() { loadJob?.cancel(); scope.cancel() }
}

private fun ExamResultsSortField.toApiSortField(): String = when (this) {
    ExamResultsSortField.EXAM -> "title"
    ExamResultsSortField.DATE -> "exam_date"
    ExamResultsSortField.CLASS -> "title"
    ExamResultsSortField.MARKS_ENTERED -> "created_at"
    ExamResultsSortField.STATUS -> "created_at"
}

private fun TeacherExamResultListItemResponse.toUi(): ExamResultRowUi {
    val progress = marksEntered
    return ExamResultRowUi(
        id = id.orEmpty(),
        examId = examId.orEmpty().ifBlank { id.orEmpty() },
        exam = title.orEmpty().ifBlank { "Untitled Exam" },
        examType = examType.orEmpty(),
        date = formatExamDate(examDate ?: startDate),
        endDate = formatExamDate(endDate),
        className = listOfNotNull(className?.trim(), sectionName?.trim()).filter { it.isNotBlank() }.joinToString("-").ifBlank { "-" },
        examStatus = status.orEmpty(),
        marksEntered = progress?.entered ?: 0,
        totalMarks = progress?.total ?: maxMarks?.toInt() ?: 0,
        status = status.orEmpty().ifBlank { "Not Started" }
    )
}

private fun formatExamDate(value: String?): String {
    val date = value?.substringBefore('T')?.substringBefore(' ')
    val parts = date?.split('-') ?: return "-"
    if (parts.size != 3) return value.orEmpty().ifBlank { "-" }
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        .getOrNull(parts[1].toIntOrNull()?.minus(1) ?: -1) ?: return value.orEmpty().ifBlank { "-" }
    return "${parts[2].padStart(2, '0')} $month ${parts[0]}"
}
